/* ============================================================
   <product-anatomy> — interactive anatomy diagram component.
   Vanilla ES2017+, no dependencies. Light DOM (no shadow root)
   so theme typography cascades naturally.
   ============================================================ */
(function () {
  'use strict';

  if (customElements.get('product-anatomy')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Decode HTML entities stored in data attributes (data-default-desc carries
  // escaped richtext that came from the Liquid `| escape` filter).
  const decoder = document.createElement('textarea');
  function decodeEntities(html) {
    if (html == null || html === '') return '';
    decoder.innerHTML = String(html);
    return decoder.value;
  }

  class ProductAnatomy extends HTMLElement {
    connectedCallback() {
      this.defaultCut = (this.dataset.activeCut || '').trim().toLowerCase();
      this.currentCut = this.defaultCut;
      this.clickable = this.dataset.clickable === 'true';

      this.panelEl = this.querySelector('.product-anatomy__panel');
      this.titleEl = this.querySelector('.product-anatomy__title');
      this.descEl = this.querySelector('.product-anatomy__desc');
      this.eyebrowEl = this.querySelector('.product-anatomy__eyebrow');
      this.backEl = this.querySelector('.product-anatomy__back');
      this.tooltipEl = this.querySelector('.product-anatomy__tooltip');
      this.svgRoot = this.querySelector('.product-anatomy__svg svg');
      this.highlightEls = this.querySelectorAll('.product-anatomy__highlight');

      if (!this.svgRoot) return;

      this.cuts = new Map();
      this.svgRoot.querySelectorAll('[data-cut]').forEach((path) => {
        const slug = (path.dataset.cut || '').trim().toLowerCase();
        if (!slug) return;
        path.setAttribute('role', 'button');
        path.setAttribute('tabindex', '0');
        path.setAttribute('aria-label', path.dataset.cutName || slug);
        this.cuts.set(slug, {
          name: path.dataset.cutName || slug,
          desc: decodeEntities(path.dataset.cutDesc || ''),
          el: path,
        });
        path.addEventListener('mouseenter', this._onPathEnter);
        path.addEventListener('mouseleave', this._onPathLeave);
        path.addEventListener('mousemove', this._onPathMove);
        path.addEventListener('focus', this._onPathEnter);
        path.addEventListener('blur', this._onPathLeave);
        path.addEventListener('click', this._onPathClick);
        path.addEventListener('keydown', this._onPathKey);
      });

      if (this.backEl) {
        this.backEl.addEventListener('click', this._onBack);
      }

      this._applyActive(this.defaultCut);
      this._updateHighlights(this.defaultCut);
      this._setupReveal();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    _setupReveal = () => {
      if (REDUCED_MOTION) return;
      if (this.dataset.reveal !== 'true') return;
      if (!('IntersectionObserver' in window)) {
        this.classList.add('is-revealed');
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('is-revealed');
              this._observer.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );
      this._observer.observe(this);
    };

    _applyActive = (slug) => {
      this.cuts.forEach((c, key) => {
        c.el.classList.toggle('is-active', key === slug);
      });
    };

    _swapPanel = (eyebrow, title, desc, slug) => {
      const apply = () => {
        if (this.eyebrowEl) this.eyebrowEl.textContent = eyebrow;
        if (this.titleEl) this.titleEl.textContent = title;
        if (this.descEl) this.descEl.innerHTML = desc;
        if (slug != null) this._updateHighlights(slug);
      };
      if (REDUCED_MOTION) { apply(); return; }
      this.panelEl.classList.add('is-swapping');
      setTimeout(() => {
        apply();
        this.panelEl.classList.remove('is-swapping');
      }, 220);
    };

    _updateHighlights = (slug) => {
      if (!this.highlightEls || this.highlightEls.length === 0) return;
      const cut = this.cuts.get(slug);
      this.highlightEls.forEach((li) => {
        const idx = li.dataset.highlightIndex;
        const valueEl = li.querySelector('.product-anatomy__highlight-value');
        if (!valueEl) return;
        // Default value is rendered server-side from the metafield. Per-cut
        // SVG overrides (data-cut-hN-value) only apply when the user explores
        // other cuts — for the product's own cut we stick with the metafield.
        let newValue = valueEl.dataset.defaultValue || '';
        if (cut && idx != null && slug !== this.defaultCut) {
          const key = 'cutH' + idx + 'Value';
          const override = cut.el.dataset[key];
          if (override) newValue = override;
        }
        valueEl.textContent = newValue;
      });
    };

    _showCut = (slug) => {
      const cut = this.cuts.get(slug);
      if (!cut) return;
      this.currentCut = slug;
      const isProductCut = slug === this.defaultCut;
      const eyebrow = isProductCut
        ? (this.eyebrowEl && this.eyebrowEl.dataset.defaultEyebrow) || 'Anatomia do corte'
        : 'Outro corte';
      const title = isProductCut
        ? (this.titleEl && this.titleEl.dataset.defaultTitle) || cut.name
        : cut.name;
      const desc = isProductCut
        ? decodeEntities((this.descEl && this.descEl.dataset.defaultDesc) || '')
        : cut.desc || '';
      this._swapPanel(eyebrow, title, desc, slug);
      if (this.backEl) this.backEl.hidden = isProductCut;
    };

    _onPathEnter = (ev) => {
      const slug = ev.currentTarget.dataset.cut;
      ev.currentTarget.classList.add('is-hover');
      if (this.tooltipEl && ev.type !== 'focus') {
        this.tooltipEl.textContent = ev.currentTarget.dataset.cutName || slug;
        this.tooltipEl.hidden = false;
        requestAnimationFrame(() => this.tooltipEl.classList.add('is-visible'));
      }
    };

    _onPathLeave = (ev) => {
      ev.currentTarget.classList.remove('is-hover');
      if (this.tooltipEl) {
        this.tooltipEl.classList.remove('is-visible');
        setTimeout(() => {
          if (this.tooltipEl && !this.tooltipEl.classList.contains('is-visible')) {
            this.tooltipEl.hidden = true;
          }
        }, 140);
      }
    };

    _onPathMove = (ev) => {
      if (!this.tooltipEl || this.tooltipEl.hidden) return;
      this.tooltipEl.style.top = (ev.clientY + 16) + 'px';
      this.tooltipEl.style.left = (ev.clientX + 14) + 'px';
    };

    _onPathClick = (ev) => {
      if (!this.clickable) return;
      const slug = ev.currentTarget.dataset.cut;
      if (slug === this.currentCut && slug !== this.defaultCut) {
        this._showCut(this.defaultCut);
        this._applyActive(this.defaultCut);
        return;
      }
      this._showCut(slug);
      this._applyActive(slug);
    };

    _onPathKey = (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        this._onPathClick(ev);
      }
    };

    _onBack = () => {
      this._showCut(this.defaultCut);
      this._applyActive(this.defaultCut);
    };
  }

  customElements.define('product-anatomy', ProductAnatomy);
})();
